/**
 * Section Component
 */

import { IBuilderComponent } from '../../base'
import {
  formatComponentAlignmentFormat,
  formatGapFormat,
  formatHorizontalAlignmentFormat,
  formatLayoutFormat,
  formatOrientationFormat,
  formatResponsiveCornerRadiusFormat,
  formatSpacingFormat,
  formatVerticalAlignmentFormat,
} from '../../format-utils'
import {
  deepMerge,
  parseAlignmentFormat,
  parseCornerRadiusFormat,
  parseGapFormat,
  parseSpacingFormat,
} from '../../parser-utils'
import type {
  ColorVariant,
  ComponentAlignment,
  HorizontalAlignment,
  OverflowSettings,
  ResponsiveValue,
  SpacingInputValue,
  SpacingUnit,
  VerticalAlignment,
} from '../../types'
import {
  formatBackgroundLayers,
  parseBackgroundLayers,
  parseLayoutFormat,
  parseOrientationFormat,
} from './section-utils'
import sectionTemplate from './section.template.json'
import type { BackgroundLayer } from './section.types'

// Simple spacing value for gap (just value + unit, not directional)
type SimpleSpacingValue = {
  value: number
  unit: SpacingUnit
}

export interface ISectionComponentSettings {
  'section-design-variant': ResponsiveValue<string>
  'section-design-sticky-position': ResponsiveValue<string>
  'section-design-sticky-offset': ResponsiveValue<number>
  'section-design-elevation'?: number | string
  'section-design-background': BackgroundLayer[]
  'section-design-bg-scheme'?: string
  'section-design-layout': ResponsiveValue<number[]>
  'section-design-shape': ResponsiveValue<string>
  'section-design-width': OverflowSettings<ResponsiveValue<string[]>>
  'section-design-orientation': ResponsiveValue<string>
  'section-design-reverse-order': ResponsiveValue<boolean>
  'section-design-max-items'?: ResponsiveValue<number>
  'section-design-same-height'?: ResponsiveValue<string[] | number[]>
  'section-design-h-align': ResponsiveValue<ComponentAlignment>
  'section-design-horizontal-align': ResponsiveValue<HorizontalAlignment>
  'section-design-v-align': ResponsiveValue<VerticalAlignment>
  'section-design-border': string[]
  'section-design-border-width': number[]
  'section-design-border-color': ColorVariant
  'section-design-border-style'?: string
  'section-design-padding': OverflowSettings<ResponsiveValue<SpacingInputValue>>
  'section-design-margin': OverflowSettings<ResponsiveValue<SpacingInputValue>>
  'section-design-gap': ResponsiveValue<SimpleSpacingValue>
  'section-design-hover-effect'?: string // Hover effect: 'none' | 'underline' | 'lift' | 'scale' | 'dim' | 'skew' | 'rotate' | 'glow' | 'shimmer' | 'border' | 'squeeze' | 'blur' | 'neon' | 'flip' | 'bounce'
}

export interface ISectionComponent extends IBuilderComponent {
  componentId: 'section'
  groupId: 'container'
  settings: ISectionComponentSettings
}

export interface AiSectionInput {
  orientation?: string // Format: "v" | "h" | "v de:h" | "h ta:v" etc. Supports responsive breakpoints (ph, pl, ta, tl, de, tv)
  layout?: string // Format: "cols:100" or "cols:50:50" or "cols:100 ta:cols:50:50"
  padding?: string // Format: "p:16px" or "pt:20px pb:20px" or "p:16px ta:p:32px"
  margin?: string // Format: "m:2rem" or "mb:16px" or "m:2rem ta:m:3rem"
  gap?: string // Format: "gap:10px" or "gap:1rem" or "gap:10px ta:gap:16px"
  background?: string[] // Format: ["solid:primary-40"] (simple) or ["color:adaptive:primary-80|primary-100", "image:url", "overlay:#000:0.5"] (multiple layers). See section-background.md
  align?: string // Format: "h:center v:start" or "h:center ta:h:start"
  cornerRadius?: string // Format: "medium" or "small ta:medium de:large"
  sticky?: boolean
  reverseOrder?: boolean
  hoverEffect?: string // Format: "bounce" or "underline" | 'none' | 'lift' | 'scale' | 'dim' | 'skew' | 'rotate' | 'glow' | 'shimmer' | 'border' | 'squeeze' | 'blur' | 'neon' | 'flip' | 'bounce'
}

const SECTION_DEFAULT_TEMPLATE = sectionTemplate as unknown as ISectionComponentSettings

export class SectionComp {
  getTemplate(): ISectionComponentSettings {
    return SECTION_DEFAULT_TEMPLATE
  }

  /**
   * Format section component settings to AI section input
   * Exports all properties in simplified format, even if they match defaults
   */
  format(settings: ISectionComponentSettings): AiSectionInput {
    const result: AiSectionInput = {}

    // Format orientation
    const orientation = formatOrientationFormat(settings['section-design-orientation'])
    if (orientation) {
      result.orientation = orientation
    }

    // Format layout
    const layout = formatLayoutFormat(settings['section-design-layout'])
    if (layout) {
      result.layout = layout
    }

    // Format padding (extract from OverflowSettings if present)
    const paddingValue = settings['section-design-padding']
    // Extract the responsive value, excluding overflowSetting if it exists
    const paddingToFormat =
      paddingValue && typeof paddingValue === 'object' && 'overflowSetting' in paddingValue
        ? (() => {
            const { overflowSetting, ...rest } = paddingValue as any
            return rest
          })()
        : paddingValue
    const padding = formatSpacingFormat(paddingToFormat as any)
    if (padding) {
      result.padding = padding
    }

    // Format margin (extract from OverflowSettings if present)
    const marginValue = settings['section-design-margin']
    // Extract the responsive value, excluding overflowSetting if it exists
    const marginToFormat =
      marginValue && typeof marginValue === 'object' && 'overflowSetting' in marginValue
        ? (() => {
            const { overflowSetting, ...rest } = marginValue as any
            return rest
          })()
        : marginValue
    const margin = formatSpacingFormat(marginToFormat as any)
    if (margin) {
      result.margin = margin
    }

    // Format gap
    const gap = formatGapFormat(settings['section-design-gap'])
    if (gap) {
      result.gap = gap
    }

    // Format background layers (always include if present)
    const background = formatBackgroundLayers(settings['section-design-background'])
    if (background && background.length > 0) {
      result.background = background
    }

    // Format alignment (combine h-align and v-align)
    const cAlign = settings['section-design-h-align']
    const hAlign = settings['section-design-horizontal-align']
    const vAlign = settings['section-design-v-align']

    if (cAlign || hAlign || vAlign) {
      const alignParts: string[] = []

      if (cAlign) {
        const cAlignFormatted = formatComponentAlignmentFormat(cAlign)
        if (cAlignFormatted) {
          alignParts.push(cAlignFormatted)
        }
      }

      if (hAlign) {
        const hAlignFormatted = formatHorizontalAlignmentFormat(hAlign)
        if (hAlignFormatted) {
          alignParts.push(hAlignFormatted)
        }
      }

      if (vAlign) {
        const vAlignFormatted = formatVerticalAlignmentFormat(vAlign)
        if (vAlignFormatted) {
          alignParts.push(vAlignFormatted)
        }
      }

      if (alignParts.length > 0) {
        result.align = alignParts.join(' ')
      }
    }

    // Format corner radius
    const cornerRadius = formatResponsiveCornerRadiusFormat(settings['section-design-shape'])
    if (cornerRadius) {
      result.cornerRadius = cornerRadius
    }

    // Format sticky (check if variant is sticky)
    const variant = settings['section-design-variant']
    if (variant) {
      const isSticky = Object.values(variant).some((v) => v === 'sticky')
      result.sticky = isSticky
    }

    // Format reverse order
    const reverseOrderValues = settings['section-design-reverse-order']
    if (reverseOrderValues) {
      const allSame = Object.values(reverseOrderValues).every((v) => v === reverseOrderValues.phone)
      if (allSame) {
        result.reverseOrder = reverseOrderValues.phone
      }
    }

    // Hover Effect
    if (settings['section-design-hover-effect']) {
      const hoverEffect = settings['section-design-hover-effect']
      if (hoverEffect && hoverEffect !== 'none') {
        result.hoverEffect = hoverEffect
      }
    }

    return result
  }

  /**
   * parse AI section input to section component settings
   */
  parse(input: AiSectionInput): ISectionComponentSettings {
    const result = deepMerge(SECTION_DEFAULT_TEMPLATE, {})

    if (input.orientation) {
      result['section-design-orientation'] = parseOrientationFormat(input.orientation)
    }

    if (input.layout) {
      result['section-design-layout'] = parseLayoutFormat(input.layout)
    }

    if (input.padding) {
      // Don't merge with template - replace entirely
      const parsedPadding = parseSpacingFormat(input.padding)
      result['section-design-padding'] = parsedPadding
    }

    if (input.margin) {
      const marginParsed = parseSpacingFormat(input.margin)
      result['section-design-margin'] = {
        ...marginParsed,
        overflowSetting: {
          phone: {
            top: { value: 5, unit: 'px' },
            right: { value: 0, unit: 'px' },
            bottom: { value: 5, unit: 'px' },
            left: { value: 0, unit: 'px' },
          },
          'phone-landscape': {
            top: { value: 5, unit: 'px' },
            right: { value: 0, unit: 'px' },
            bottom: { value: 5, unit: 'px' },
            left: { value: 0, unit: 'px' },
          },
          tablet: {
            top: { value: 5, unit: 'px' },
            right: { value: 0, unit: 'px' },
            bottom: { value: 5, unit: 'px' },
            left: { value: 0, unit: 'px' },
          },
          'tablet-landscape': {
            top: { value: 5, unit: 'px' },
            right: { value: 0, unit: 'px' },
            bottom: { value: 5, unit: 'px' },
            left: { value: 0, unit: 'px' },
          },
          desktop: {
            top: { value: 5, unit: 'px' },
            right: { value: 0, unit: 'px' },
            bottom: { value: 5, unit: 'px' },
            left: { value: 0, unit: 'px' },
          },
          tv: {
            top: { value: 5, unit: 'px' },
            right: { value: 0, unit: 'px' },
            bottom: { value: 5, unit: 'px' },
            left: { value: 0, unit: 'px' },
          },
        },
      }
    }

    if (input.gap) {
      result['section-design-gap'] = parseGapFormat(input.gap)
    }

    if (input.background) {
      const templateLayers = result['section-design-background'] || []
      result['section-design-background'] = parseBackgroundLayers(input.background, templateLayers)
    }

    if (input.align) {
      const alignmentParsed = parseAlignmentFormat(input.align)

      if (alignmentParsed.cAlign) {
        result['section-design-h-align'] = alignmentParsed.cAlign
      }
      if (alignmentParsed.hAlign) {
        result['section-design-horizontal-align'] = alignmentParsed.hAlign
      }
      if (alignmentParsed.vAlign) {
        result['section-design-v-align'] = alignmentParsed.vAlign
      }
    }

    if (input.cornerRadius) {
      result['section-design-shape'] = parseCornerRadiusFormat(input.cornerRadius)
    }

    if (input.sticky !== undefined) {
      result['section-design-variant'] = {
        phone: input.sticky ? 'sticky' : 'normal',
        'phone-landscape': input.sticky ? 'sticky' : 'normal',
        tablet: input.sticky ? 'sticky' : 'normal',
        'tablet-landscape': input.sticky ? 'sticky' : 'normal',
        desktop: input.sticky ? 'sticky' : 'normal',
        tv: input.sticky ? 'sticky' : 'normal',
      }
    }

    if (input.reverseOrder !== undefined) {
      result['section-design-reverse-order'] = {
        phone: input.reverseOrder,
        'phone-landscape': input.reverseOrder,
        tablet: input.reverseOrder,
        'tablet-landscape': input.reverseOrder,
        desktop: input.reverseOrder,
        tv: input.reverseOrder,
      }
    }

    // Hover Effect
    if (input.hoverEffect) {
      result['section-design-hover-effect'] = input.hoverEffect
    }

    return result
  }
}
